import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { MAX_RADIUS_MI, MIN_RADIUS_MI, RADIUS_PRESETS_MI } from '../../../util/searchArea';

import css from './RadiusControl.module.css';

/**
 * The search radius control over the map (02_Search_Mobile/SPEC.md "Search radius"):
 * 5 mi · 10 mi · 25 mi · Custom, where Custom opens a 1–100 mi slider, and a hint under it.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className positions the control over the map
 * @param {number} props.radiusMi the current radius
 * @param {number} props.resultCount sitters within the radius
 * @param {boolean} props.showCount show "{n} sitters within {r} mi" instead of the hint
 * @param {boolean} props.isMobileLayout "Tap" instead of "Click"
 * @param {Function} props.onChange called with (radiusMi, { isDraft }): isDraft while sliding
 * @returns {JSX.Element}
 */
const RadiusControl = props => {
  const { className, radiusMi, resultCount, showCount, isMobileLayout, onChange } = props;
  const intl = useIntl();
  const isPreset = RADIUS_PRESETS_MI.includes(radiusMi);
  const [isCustomOpen, setCustomOpen] = useState(!isPreset);
  const isCustom = isCustomOpen || !isPreset;

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.controls}>
        <div
          className={css.segments}
          role="radiogroup"
          aria-label={intl.formatMessage({ id: 'RadiusControl.label' })}
        >
          {RADIUS_PRESETS_MI.map(preset => {
            const isSelected = !isCustom && preset === radiusMi;
            return (
              <button
                key={preset}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={classNames(css.segment, { [css.selected]: isSelected })}
                onClick={() => {
                  setCustomOpen(false);
                  onChange(preset);
                }}
              >
                <FormattedMessage id="RadiusControl.miles" values={{ radius: preset }} />
              </button>
            );
          })}
          <button
            type="button"
            role="radio"
            aria-checked={isCustom}
            className={classNames(css.segment, { [css.selected]: isCustom })}
            onClick={() => setCustomOpen(true)}
          >
            <FormattedMessage id="RadiusControl.custom" />
          </button>
        </div>

        {isCustom ? (
          <div className={css.slider}>
            <input
              type="range"
              className={css.range}
              min={MIN_RADIUS_MI}
              max={MAX_RADIUS_MI}
              step={1}
              value={radiusMi}
              onChange={e => onChange(Number(e.target.value), { isDraft: true })}
              aria-label={intl.formatMessage({ id: 'RadiusControl.customLabel' })}
            />
            <span className={css.readout}>
              <FormattedMessage id="RadiusControl.miles" values={{ radius: radiusMi }} />
            </span>
          </div>
        ) : null}

        <p className={css.hint}>
          {showCount ? (
            <FormattedMessage
              id="RadiusControl.count"
              values={{ count: resultCount, radius: radiusMi }}
            />
          ) : isMobileLayout ? (
            <FormattedMessage id="RadiusControl.hintTap" />
          ) : (
            <FormattedMessage id="RadiusControl.hintClick" />
          )}
        </p>
      </div>
    </div>
  );
};

export default RadiusControl;
